import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ProductSearchFilterOptions } from '@app/akita/api/products/models/product-search.filter-options.model';
import { generateFiltersID } from '@app/akita/api/products/models/product-search.filters.model';
import { AkitaProductsQuery } from '@app/akita/api/products/state/products.query';
import { PhoneFilterOptions } from '@app/akita/api/products/models/phone-filter-options.model';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { BehaviorSubject, Observable, Subject, map } from 'rxjs';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { NgFor, AsyncPipe } from '@angular/common';
import { PopsyCheckboxComponent } from '../../checkbox/popsy-checkbox.component';

@Component({
  selector: 'popsy-product-ram-filter',
  templateUrl: './product-ram-filter.template.html',
  styleUrls: ['./product-ram-filter.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoModule, PopsyCheckboxComponent, NgFor, AsyncPipe],
})
export class ProductRamFilterComponent implements OnChanges, OnDestroy {
  @Input() public selected?: Array<string> | null;
  @Input() public searchOptionsPath?: string | null;

  @Input() public loading?: boolean | null;
  @Input() public mobile?: boolean | null;
  @Output() public readonly selectionChanged: EventEmitter<Array<string>>;

  private readonly openSubject: BehaviorSubject<boolean>;
  public readonly open$: Observable<boolean>;

  private readonly selectedRamsSubject: BehaviorSubject<Array<string>>;
  public readonly selectedRams$: Observable<Array<string>>;

  public productRam$: Observable<Array<string>>;

  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly akitaProductsQuery: AkitaProductsQuery,
    private readonly translateService: TranslocoService
  ) {
    this.destroySubject = new Subject();
    this.selectionChanged = new EventEmitter();

    this.openSubject = new BehaviorSubject(false as any);
    this.open$ = this.openSubject.asObservable();

    this.selectedRamsSubject = new BehaviorSubject([] as any);
    this.selectedRams$ = this.selectedRamsSubject.asObservable();

    this.productRam$ = this.akitaProductsQuery
      .selectProductFilters(this.searchOptionsPath || this.searchBasicOptionsPath)
      .pipe(
        map((searchOptions: ProductSearchFilterOptions) => {
          let parsedOptions = [
            ...(searchOptions.ram.sort((a, b) => (a < b ? -1 : 0)) || []),
          ];
          if (parsedOptions.length <= 1) {
            parsedOptions = [];
          }

          return parsedOptions;
        })
      );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.selected) {
      if (
        this.selected &&
        JSON.stringify(this.selected) !== JSON.stringify(this.selectedRamsSubject.value)
      ) {
        this.selectedRamsSubject.next(this.selected);
      }

      this.productRam$ = this.akitaProductsQuery
        .selectProductFilters(this.searchOptionsPath || this.searchBasicOptionsPath)
        .pipe(
          map((searchOptions: ProductSearchFilterOptions) => {
            const parsedOptions = [
              ...(searchOptions.ram.sort((a, b) => (a < b ? -1 : 0)) || []),
            ];
            return parsedOptions;
          })
        );
    }
  }

  private get searchBasicOptionsPath(): string {
    const options: PhoneFilterOptions = {
      // country: this.akitaRouterQuery.country,
      locale: this.translateService.getActiveLang(),
    };
    return generateFiltersID(options);
  }

  public onCheckboxChecked(checked?: boolean | null, ram?: string | null): void {
    if (ram === 'label_all' && checked) {
      this.selectedRamsSubject.next([]);
      this.selectionChanged.emit([]);
    } else if (ram) {
      const selectedRams = [...(this.selectedRamsSubject.value || [])];
      const position = selectedRams.indexOf(ram);
      if (position === -1 && checked) {
        selectedRams.push(ram);
      } else if (position !== -1 && !checked) {
        selectedRams.splice(position, 1);
      }
      this.selectedRamsSubject.next(selectedRams);
      this.selectionChanged.emit(selectedRams);
    }
    if (!this.mobile) {
      this.scrollToTop();
    }
    this.changeDetector.markForCheck();
  }

  public onButtonClicked(ram?: string | null): void {
    if (ram) {
      const selectedRams = [...(this.selectedRamsSubject.value || [])];
      const isSelected = Boolean(selectedRams.includes(ram));
      this.onCheckboxChecked(!isSelected, ram);
    }
  }

  public getIsRamSelected(ram?: string | null): boolean {
    const selectedRams = [...(this.selectedRamsSubject.value || [])];
    if (ram === 'label_all') {
      return Boolean(selectedRams.length <= 0);
    } else {
      return Boolean(selectedRams.includes(`${ram || ''}`));
    }
  }

  public scrollToTop(): void {
    try {
      if (
        this.akitaRouterQuery.isBrowser &&
        window.document &&
        window.document.getElementById
      ) {
        const scrollElement = window.document.getElementById('mainAppScroll');
        if (scrollElement) {
          if (scrollElement.scrollTo) {
            scrollElement.scrollTo({
              top: 200,
              left: 0,
              behavior: 'smooth',
            });
          } else {
            scrollElement.scrollTop = 500;
          }
        }
      }
    } catch (err) {
      /* SSR Not Supported */
    }
  }

  public trackByRam(index: number, ram?: string | null): string {
    return `${ram || index}`;
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }
}
